<template>
  <pinned-message :hidden="user?.is_admin || !isScreenL" />
  <swiper
    :key="`main-${activeLot?.id}`"
    v-bind="$attrs"
    :slides-per-view="1"
    :space-between="20"
    :pagination="false"
    :navigation="files.length > 1"
    :scrollbar="false"
    :loop="true"
    :thumbs-swiper="'.swiper-bullets .swiper-wrapper'"
    :check-active-index="true"
    type="mainLotGallery"
    class="with-main-bullets main-lot-gallery-swiper"
    init="false"
    @swiper-init="onSliderInit"
    @update-active-slide-index="(i) => changeActiveIndexSlide(i)">
    <swiper-slide v-for="file in files" :key="file.src" class="swiper-slide">
      <img
        v-if="file.type == LotFileTypes.IMAGE"
        :src="file.src"
        class="img"
        :alt="file.src"
        :width="gallerySize.width"
        :height="gallerySize.height" />
      <a v-else-if="file.type == LotFileTypes.PDF" :href="file.src" class="magnifier-zoom-image" target="_blank">
        <img
          src="@/assets/images/common/lot-pdf.svg"
          class="img"
          alt="pdf"
          :width="gallerySize.width"
          :height="gallerySize.height" />
      </a>
      <a v-else-if="file.type == LotFileTypes.VIDEO" :href="file.src" class="magnifier-zoom-image" target="_blank">
        <img
          src="@/assets/images/common/lot-video.svg"
          class="img"
          alt="video"
          :width="gallerySize.width"
          :height="gallerySize.height" />
      </a>
    </swiper-slide>
  </swiper>

  <swiper
    v-if="files.length > 1"
    :key="`thumbs-${activeLot?.id}`"
    :slides-per-view="5"
    :space-between="10"
    :pagination="false"
    :navigation="false"
    :scrollbar="false"
    :loop="files.length > 5"
    type="mainLotGalleryBullets"
    class="swiper-wrapper swiper-bullets"
    init="false"
    @swiper-init="onSliderInitThumbs"
  >
    <swiper-slide v-for="file in files" :key="`thumb-${file.src}`" class="swiper-pagination-bullet swiper-slide"></swiper-slide>
  </swiper>
  <span v-if="files.length > 1" class="count-images">
    {{ activeImageIndex }}/{{ files.length }} images
  </span>
</template>

<script setup lang="ts">
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { computed, nextTick, ref, watch } from "vue";
  import Swiper from "@/components/_partials/Swiper.vue";
  import type { Swiper as SwiperSlider } from "swiper/types";
  import { LotFileTypes } from "@/enum";
  import { isObject } from "lodash";
  import { isScreenL } from "@/utils/media-size";
  import { IMessage } from "@/models";
  import PinnedMessage from "@/components/_partials/PinnedMessage.vue";

  const swiperMain = ref<SwiperSlider | null>(null);
  const swiperThumbs = ref<SwiperSlider | null>(null);
  const activeImageIndex = ref(1);

  const newMessage = ref<IMessage | null>(null);
  const user = computed(() => auctionStore.user);

  const gallerySize = computed(() => {
    if (auctionStore.user?.is_admin) {
      return { width: 290, height: 373 };
    } else {
      return { width: 403, height: 517 };
    }
  });

  const auctionStore = useAuctionStore();
  const activeLot = computed(() => (auctionStore.activeLot?.id ? auctionStore.activeLot : auctionStore.fakeActiveLot));
  const files = computed(() => {
    return isObject(activeLot.value?.files) ? Object.values(activeLot.value?.files) || [] : [];
  });

  const onSliderInit = (swiper: SwiperSlider) => {
    swiperMain.value = swiper;
  };

  const changeActiveIndexSlide = (index: number) => {
    // For looped sliders, use realIndex from swiperMain to get correct position
    if (swiperMain.value) {
      const realIdx = swiperMain.value.realIndex ?? swiperMain.value.activeIndex ?? index;
      // Adding 1 because swiper indices start from 0, but we display 1-based numbering
      activeImageIndex.value = realIdx + 1;
    } else {
      activeImageIndex.value = index + 1;
    }
  };

  const onSliderInitThumbs = (swiper: SwiperSlider) => {
    swiperThumbs.value = swiper;
  };

  watch(
    () => activeLot.value?.id,
    () => {
      // Reset index when lot changes (swiper components will be recreated due to :key)
      activeImageIndex.value = 1;
    }
  );
</script>

<style>
  @media (min-width: 769px) {
    /* TODO: Need fix it after 10.10.25 */
    .custom-swiper.swiper-bullets {
      display: none;
    }
  }
  @media (max-width: 1024px) {
    /* TODO: Need fix it after 10.10.25 */
    .custom-swiper .swiper-navigation-btn {
      display: none;
    }
  }
</style>